/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */

import ContentInfoParser from '../info/content-info-parser';
import { BaseContentController } from './base-content-controller';

/**
 * @import { ContentController } from '.';
 * @import { IdMap } from '../../../util.type';
 * @import { ExternalSource, ExternalSourceAnalyticsTracker } from '../../medium-external';
 * @import { Logger } from '../../logger/logger';
 * @import { Medium } from '../../medium/medium';
 * @import MediumExternalSourceFactory from '../../medium-external/medium-external-source-factory';
 * @import Store from '../../store/store';
 * @import { ViewerRegistry } from '../../viewer/registry';
 * @import { Content } from '../content';
 * @import ContentDownloader from '../downloader/content-downloader';
 * @import { ContentInfo } from '../info/content-info';
 * @import ContentControllerFactory from './content-controller-factory';
 */

/**
 * @implements {ContentController}
 */
export default class MediumExternalContentController extends BaseContentController {
  /**
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentInfo} contentInfo
   * @param {Content} content
   * @param {ContentDownloader} contentDownloader
   * @param {Logger} logger
   * @param {MediumExternalSourceFactory} mediumExternalSourceFactory
   * @param {HTMLElement} parentEl
   * @param {number} parentVolume
   * @param {Store} store
   * @param {ViewerRegistry}  viewerRegistry
   */
  constructor(
    contentControllerFactory,
    contentInfo,
    content,
    contentDownloader,
    logger,
    mediumExternalSourceFactory,
    parentEl,
    parentVolume,
    store,
    viewerRegistry
  ) {
    const medium = content.medium;
    if (!medium) {
      throw new Error('content should be medium');
    }

    super();
    this.contentControllerFactory = contentControllerFactory;
    this.contentInfo = contentInfo;
    this.content = content;
    this.contentDownloader = contentDownloader;
    this.mediumExternalSourceFactory = mediumExternalSourceFactory;
    this.logger = logger;
    this.parentEl = parentEl;
    this.parentVolume = parentVolume;
    this.store = store;
    this.viewerRegistry = viewerRegistry;

    /** @type {ExternalSourceAnalyticsTracker | undefined} */
    this.analyticsTracker = undefined;
    /** @type {ContentController | undefined} */
    this.controller = undefined;
    /** @type {ExternalSource | undefined} */
    this.externalSource = undefined;
    this.medium = medium;

    this.onPlayed = this.onPlayed.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
    this.onEnded = this.onEnded.bind(this);
  }

  /**
   * @param {Content} content
   */
  onPlaying(content) {
    this.analyticsTracker?.trackStartAnalytics(content);
  }

  /**
   * @param {Content} content
   */
  onPlayed(content) {
    this.analyticsTracker?.trackEndAnalytics(content);
  }

  /**
   * Reakce na konec vytvořeného playlistu.
   */
  onEnded() {
    // Vyvolání události, že lokální medium skončilo.
    this.emitter.emit('played', this.content);
    // Vyvolání události, že lokální medium skončilo.
    this.emitter.emit('ended');
  }

  /** @type {ContentController['preload']} */
  async preload() {
    await super.preloadCallback(() => this.preloadController());
  }

  /**
   * @private
   */
  async preloadController() {
    const externalSourceFactory =
      this.mediumExternalSourceFactory.createFactory(
        this.contentInfo,
        this.medium
      );
    const externalSource = await externalSourceFactory.create(
      this.contentInfo,
      this.medium
    );
    this.externalSource = externalSource;
    this.analyticsTracker = externalSource.analyticsTracker;
    const playlist = externalSource.playlist;
    const media = playlist.tracks.reduce(
      (mediaMap, track) =>
        track.medium ? mediaMap.set(track.medium.id, track.medium) : mediaMap,
      /** @type {IdMap<Medium>} */ (new Map())
    );
    // Nalinkování existujících medií.
    await ContentInfoParser.linkAssetsToMedia(
      this.viewerRegistry,
      this.store,
      media
    );
    // Spuštění stahování na pozadí.
    this.contentDownloader.download(this.contentInfo, { media });

    const controller = this.contentControllerFactory.create(
      this.parentEl,
      { playlist },
      { contentInfo: this.contentInfo, volume: this.parentVolume }
    );
    controller.on('played', this.onPlayed);
    controller.on('playing', this.onPlaying);
    controller.on('ended', this.onEnded);
    await controller.preload();
    this.controller = controller;
  }

  /** @type {ContentController['restart']} */
  async restart() {
    await super.restartCallback(async () => {
      // TODO: vyřešit lépe restart, problém je při samostatném mediu a pokud nemám žádné assety staženy.
      await this.stopController();
      await this.preloadController();
      await this.startController(0);
    });
  }

  /** @type {ContentController['start']} */
  async start(offsetMs) {
    await super.startCallback(() => this.startController(offsetMs));
  }

  /**
   * @private
   * @param {number} offsetMs
   */
  async startController(offsetMs) {
    const { controller } = this;
    if (!controller) {
      throw new Error('content controller not set for MediumExternal');
    }
    await controller.start(offsetMs);
    // Externí medium se začalo přehrávat.
    this.emitter.emit('playing', this.content);
  }

  /** @type {ContentController['stop']} */
  async stop() {
    // Úmyslené ignorování chyby. Pokud chyba vznikla při stop,
    // tak aby metoda stop byla přerušena. Ten případ by neměl nastávat.
    await super.stopCallback(() => this.stopController());
  }

  /**
   * @private
   */
  async stopController() {
    // Prvně odregistrace událostí, aby po zavolání stop nemohlo dojít k volání restart.
    this.controller?.off('played', this.onPlayed);
    this.controller?.off('playing', this.onPlaying);
    this.controller?.off('ended', this.onEnded);
    // Umyslné ignorování chyb.
    await this.controller?.stop().catch(() => {});
    this.externalSource?.destroy();

    this.externalSource = undefined;
    this.analyticsTracker = undefined;
  }
}
